/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo, useState } from 'react';
import {
	fireErrorAnalytics as fireErrorAnalyticsImpl,
	type AnalyticsPayload,
} from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type {
	ToResolvedService,
	AnyService,
	DependencyOverridesOf,
} from '@atlassian/expansion-services/types';
import { useServiceCore } from '@atlassian/expansion-services/core';
import type { FetchInput, Product } from '@atlassian/expansion-services/services';
import { useAnalyticsEvents } from './services/analytics-events.tsx';
import { jiraFetchJson } from './services/fetch.tsx';
import { useJiraCurrentProduct } from './services/current-product.tsx';

type EnvironmentDeps = {
	createAnalyticsEvent: (_payload: AnalyticsEventPayload) => UIAnalyticsEvent;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	fetchJSON: (input: FetchInput) => Promise<any>;
	getCurrentCloudId: () => string;
	getCurrentProduct: () => Product;
	getCurrentUserId: () => string | null;
	fireErrorAnalytics: (payload: AnalyticsPayload) => Promise<void>;
};

const useEnvironmentDeps = (): EnvironmentDeps => {
	// Use product's hooks to build a bag of dependencies
	// that the product environment will provide to the services.
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const product = useJiraCurrentProduct();
	const atlassianAccountId = useAccountId();

	return useMemo(
		() => ({
			createAnalyticsEvent,
			fetchJSON: jiraFetchJson,
			getCurrentCloudId: () => cloudId,
			getCurrentProduct: () => product,
			getCurrentUserId: () => atlassianAccountId,
			fireErrorAnalytics: fireErrorAnalyticsImpl,
		}),
		[createAnalyticsEvent, cloudId, product, atlassianAccountId],
	);
};
// The only fireErrorAnalytics customization a call site can ask for: the rest of the payload
// (error, meta, attributes, ...) always comes from expansion services to maintain analytics consistency.
type FireErrorAnalyticsFlags = Pick<AnalyticsPayload, 'sendToPrivacyUnsafeSplunk' | 'skipSentry'>;

// Forces eager evaluation of the Omit<...> below into a plain object shape. Without this,
// TypeScript can leave the excess-property check too permissive against the still-generic
// DependencyOverridesOf<...> expression, silently allowing fireErrorAnalytics back in.
type Simplify<T> = { [Key in keyof T]: T[Key] };

type ProvidedDeps<DeclaredService extends AnyService> = Simplify<
	Omit<DependencyOverridesOf<DeclaredService, EnvironmentDeps>, 'fireErrorAnalytics'>
> & {
	fireErrorAnalyticsFlags?: FireErrorAnalyticsFlags;
};

const useStableFireErrorAnalyticsFlags = (
	flags: FireErrorAnalyticsFlags | undefined,
): FireErrorAnalyticsFlags | undefined => {
	const [stableFlags, setStableFlags] = useState(flags);
	if (
		flags !== stableFlags &&
		(flags?.sendToPrivacyUnsafeSplunk !== stableFlags?.sendToPrivacyUnsafeSplunk ||
			flags?.skipSentry !== stableFlags?.skipSentry)
	) {
		setStableFlags(flags);
	}
	return stableFlags;
};

type UseService = <DeclaredService extends AnyService>(
	service: DeclaredService,
	...depsRest: { [key: string]: never } extends DependencyOverridesOf<
		DeclaredService,
		EnvironmentDeps
	>
		? [] | [deps: ProvidedDeps<DeclaredService>]
		: [deps: ProvidedDeps<DeclaredService>]
) => ToResolvedService<DeclaredService>;

export const useService: UseService = <DeclaredService extends AnyService>(
	service: DeclaredService,
	providedDeps?: ProvidedDeps<DeclaredService>,
): ToResolvedService<DeclaredService> => {
	const environmentDeps = useEnvironmentDeps();
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const { fireErrorAnalyticsFlags, ...restProvidedDeps } = (providedDeps ??
		{}) as ProvidedDeps<DeclaredService>;
	const stableFireErrorAnalyticsFlags = useStableFireErrorAnalyticsFlags(fireErrorAnalyticsFlags);
	const fireErrorAnalyticsOverride = useMemo(
		() =>
			stableFireErrorAnalyticsFlags &&
			((payload: AnalyticsPayload) =>
				fireErrorAnalyticsImpl({ ...payload, ...stableFireErrorAnalyticsFlags })),
		[stableFireErrorAnalyticsFlags],
	);

	const deps = {
		...environmentDeps,
		...restProvidedDeps,
		...(fireErrorAnalyticsOverride && { fireErrorAnalytics: fireErrorAnalyticsOverride }),
	};

	// oxlint-disable-next-line typescript/consistent-type-assertions
	return useServiceCore(service, deps as never);
};
